import { sendToApi } from 'api/client'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation } from 'react-query'
import { MediaObject, SelectedImage } from 'types'
import { UseMutationOptions } from 'react-query/types/react/types'
import * as Sentry from '@sentry/react'

export const getMimeType = (file: File): string => {
  if (file.type) return file.type

  // file.type does not exist for some file types, so we infer from the file extension
  if (file.name.endsWith('.md')) return 'text/markdown'

  Sentry.captureMessage(`Unknown MIME type for file: ${file.name}`, 'warning')
  return file.type
}

export type MediaObjectAttributes = {
  filename: string
  file: File
  duration?: number
  mime_type?: string
  width?: number
  height?: number
  size?: number
  source_url?: string
  thumbnail_url?: string
  workspace_id?: number
}

type Data = Array<MediaObject>

type Variables = Array<MediaObjectAttributes>

type Options = Omit<UseMutationOptions<Data, Error, Variables>, 'mutationKey' | 'mutationFn'>

const generatePresignedUrl = async (workspaceId: number, filename: string) => {
  const res = await sendToApi<{ presigned_url: string }>(`/w/${workspaceId}/media_objects/presigned_urls`, {
    method: 'POST',
    body: {
      filename,
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error('Failed to generate presigned URL')
  }

  return res.data.presigned_url
}

const uploadFile = async (presignedUrl: string, file: File) => {
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  })

  if (!res.ok) {
    throw new Error('Failed to upload file')
  }
  return res
}

const createMediaObjects = async (workspaceId: number, media_objects_attributes: MediaObjectAttributes[]) => {
  const presignedUrls = await Promise.all(
    media_objects_attributes.map(({ filename }) => generatePresignedUrl(workspaceId, filename)),
  )

  await Promise.all(
    presignedUrls.map((presignedUrl, index) => uploadFile(presignedUrl, media_objects_attributes[index].file)),
  )

  const res = await sendToApi(`/w/${workspaceId}/media_objects/batch`, {
    method: 'POST',
    body: {
      media_objects: presignedUrls.map((presignedUrl, index) => ({
        source_url: presignedUrl.split('?')[0],
        mime_type: getMimeType(media_objects_attributes[index].file),
        height: media_objects_attributes[index].height,
        width: media_objects_attributes[index].width,
        workspace_id: workspaceId,
      })),
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data.media_objects
}

export const useCreateMediaObjects = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>((vars) => createMediaObjects(workspaceId, vars), options)
}

const createMediaObjectsWithProgress = async (
  workspaceId: number,
  media_objects_attributes: MediaObjectAttributes[],
  onProgress: (progress: number) => void,
) => {
  const presignedUrls = await Promise.all(
    media_objects_attributes.map(({ filename }) => generatePresignedUrl(workspaceId, filename)),
  )

  await Promise.all(
    presignedUrls.map((presignedUrl, index) =>
      MediaFileClient.uploadToS3(presignedUrl, media_objects_attributes[index].file, (progress) =>
        onProgress?.(progress),
      ),
    ),
  )

  const res = await sendToApi(`/w/${workspaceId}/media_objects/batch`, {
    method: 'POST',
    body: {
      media_objects: presignedUrls.map((presignedUrl, index) => ({
        source_url: presignedUrl.split('?')[0],
        mime_type: getMimeType(media_objects_attributes[index].file),
        height: media_objects_attributes[index].height,
        width: media_objects_attributes[index].width,
        workspace_id: workspaceId,
      })),
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data.media_objects
}

export const useCreateMediaObjectsWithProgress = ({ onProgress }: { onProgress: (progress: number) => void }) => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>((vars) => createMediaObjectsWithProgress(workspaceId, vars, onProgress))
}

const createMediaObjectsWithoutPresigning = async (
  workspaceId: number,
  selectedImages: (SelectedImage & {
    duration?: number
    mime_type?: string
    width?: number
    height?: number
    size?: number
    source_url?: string
    thumbnail_url?: string
  })[],
) => {
  const res = await sendToApi(`/w/${workspaceId}/media_objects/batch`, {
    method: 'POST',
    body: {
      media_objects: selectedImages.map((image, index) => ({
        source_url: image.url,
        mime_type: image.mime_type,
        width: image.width,
        height: image.height,
        size: image.size,
        duration: image.duration,
        workspace_id: workspaceId,
        thumbnail_url: image.mime_type?.startsWith('video') ? image.thumbnail_url : image.url,
      })),
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data.media_objects
}

export const useCreateMediaObjectsWithoutPresigning = () => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, SelectedImage[]>((vars) => createMediaObjectsWithoutPresigning(workspaceId, vars))
}
